import React, {
  createContext,
  Dispatch,
  useContext,
  useMemo,
  useReducer,
} from "react";

import { useApolloClient } from "@apollo/client";

import { createActions } from "../context/actionCreators";
import appReducer from "../context/reducer";
import {
  IAction,
  IAppProviderContextProps,
  IProviderProps,
} from "../interfaces";

const initialState: IAppProviderContextProps = {
  startupData: null,
  isLoading: false,
  error: null,
  gamblingBlock: {
    controlType: "GAMBLING",
    declineAll: false,
    impulseDelayRemaining: null,
  },
  cmsStaticContent: null,
};

const AppProviderContext = createContext<
  | {
      state: IAppProviderContextProps;
      dispatch: Dispatch<IAction>;
      actions: ReturnType<typeof createActions>;
    }
  | undefined
>(undefined);

const AppProvider: React.FC<IProviderProps> = ({ children }) => {
  const client = useApolloClient();
  const [state, dispatch] = useReducer(appReducer, initialState);
  const actions = createActions(dispatch, state, client);

  const contextValue = useMemo(
    () => ({ state, dispatch, actions }),
    [state, dispatch, actions]
  );

  return (
    <AppProviderContext.Provider value={contextValue}>
      {children}
    </AppProviderContext.Provider>
  );
};

const useAppProviderContext = () => {
  const context = useContext(AppProviderContext);
  if (!context) {
    throw new Error("useAppProviderContext must be used within a Provider");
  }
  return context;
};

export { AppProvider, appReducer, useAppProviderContext };
